import Cookies from "js-cookie";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import {
  AUTHENTICATION_STATUS,
  AUTH_STATES,
  AuthenticationActionType,
  AuthenticationContext,
  AuthenticationState,
} from "./AuthenticationProvider.decl";

import {
  COOKIE_KEYS,
  REFRESH_TOKEN_INTERVAL,
} from "@/constants/auth.constants";
import {
  getAuthRefreshToken,
  getAuthToken,
  removeAuthCookies,
  requestRefreshToken,
  setAuthCookies,
} from "@/helpers/authentication/authentication.helpers";
import { redirectToDashboard } from "@/helpers/navigation/navigation.helpers";
import {
  AUTH_STRATEGY,
  Loading,
  decodeToken,
  isOnlyAgent,
} from "@dashboard/library";

const authenticationState: AuthenticationState = {
  status: AUTHENTICATION_STATUS.UNKNOWN,
  signedInError: null,
  signedInStrategy: null,
  isConnectedAs: false,
  token: null,
  decodedToken: undefined,
  isAgent: false,
};

function authenticationReducer(
  state: AuthenticationState,
  action: AuthenticationActionType
): AuthenticationState {
  switch (action.type) {
    case AUTH_STATES.SIGN_IN_SUCCESS: {
      const { isConnectedAs, token, decodedToken, strategy, isAgent } =
        action.payload;

      return {
        ...state,
        signedInStrategy: strategy,
        status: AUTHENTICATION_STATUS.AUTHENTICATED,
        isConnectedAs,
        token,
        decodedToken,
        isAgent,
      };
    }
    case AUTH_STATES.SIGN_IN_ERROR:
      return {
        ...state,
        status: AUTHENTICATION_STATUS.NOT_AUTHENTICATED,
        signedInError: action.payload.message,
        signedInStrategy: action.payload.strategy,
      };
    case AUTH_STATES.REFRESH_TOKEN:
      return {
        ...state,
        token: action.payload.token,
      };
    case AUTH_STATES.SIGN_OUT:
      return {
        ...authenticationState,
        status: AUTHENTICATION_STATUS.NOT_AUTHENTICATED,
      };
    /* istanbul ignore next */
    default:
      throw new Error("Unexpected authentication action type");
  }
}

export const AuthenticationStateContext = createContext<AuthenticationContext>(
  {} as AuthenticationContext
);

export function AuthenticationProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [authState, dispatch] = useReducer(
    authenticationReducer,
    authenticationState
  );

  const setSignInError = useCallback(
    (message: string | null, strategy = AUTH_STRATEGY.PASSWORD) => {
      dispatch({
        type: AUTH_STATES.SIGN_IN_ERROR,
        payload: { message, strategy },
      });
    },
    []
  );

  const checkAuth = useCallback(async () => {
    const token = getAuthToken();
    const isConnectedAs =
      Cookies.get(COOKIE_KEYS.IS_CONNECTED_AS) === "true" ||
      !!Cookies.get(COOKIE_KEYS.IMPERSONATE_TOKEN);

    if (token) {
      const strategy = Cookies.get(COOKIE_KEYS.AUTH_STRATEGY) as AUTH_STRATEGY;
      const decodedToken = decodeToken(token);
      const roles = decodedToken["custom:roles"];
      const isAgent = isOnlyAgent(roles);

      return dispatch({
        type: AUTH_STATES.SIGN_IN_SUCCESS,
        payload: {
          token,
          isConnectedAs,
          decodedToken,
          strategy,
          isAgent,
        },
      });
    }

    return dispatch({
      type: AUTH_STATES.SIGN_OUT,
    });
  }, []);

  const refreshToken = useCallback(async () => {
    const token = getAuthToken();
    const refreshToken = getAuthRefreshToken();

    if (!token || !refreshToken) {
      throw new Error("No token or refresh token.");
    }

    const { idToken: newToken, refreshToken: newRefreshToken } =
      await requestRefreshToken({
        token,
        refreshToken,
      });

    setAuthCookies(newToken, newRefreshToken);

    return dispatch({
      type: AUTH_STATES.REFRESH_TOKEN,
      payload: { token: newToken },
    });
  }, []);

  const onSessionExpired = useCallback(() => {
    removeAuthCookies();
    // It should be dashboard responsability to logout user
    redirectToDashboard(`/login?redirect=${window.location.href}`);
  }, []);

  const value: AuthenticationContext = useMemo(
    () => ({
      authState,
      actions: {
        onSessionExpired,
        refreshToken,
        setSignInError,
      },
    }),
    [authState, onSessionExpired, refreshToken, setSignInError]
  );

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (authState.status === AUTHENTICATION_STATUS.AUTHENTICATED) {
      const intervalId = setInterval(async () => {
        try {
          await refreshToken();
        } catch (error) {
          onSessionExpired();
        }
      }, REFRESH_TOKEN_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [authState.status, refreshToken, onSessionExpired]);

  // We need this loading otherwise this provider re-renders
  // when loaded is true and refetch global state
  if (authState.status === AUTHENTICATION_STATUS.UNKNOWN) {
    return <Loading data-test="auth-loading" />;
  }

  return (
    <AuthenticationStateContext.Provider value={value}>
      {children}
    </AuthenticationStateContext.Provider>
  );
}
